import {
    Address,
    AttributeValue,
    CartLine,
    OrgUnitLocation,
    ProductCatalog,
    ReleasedProductType,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { addProductToCartInternal, ICartActionResult, ICartState, ICartActionResultWithCart } from '@msdyn365-commerce/global-state';
import * as MsDyn365 from '@msdyn365-commerce/core';
import { IActionContext } from '@msdyn365-commerce/core';
import { validateTimeSlotValue } from './time-utils';
import { addOrUpdateTextAttribute, getAttributeTextValueByName } from './attributes';
import {
    NEC_LAST_PRICE_UPDATE_TIME,
    NEC_SELECTED_FRINGE_DESCRIPTION_ATTR_NAME,
    NEC_SELECTED_TIME_SLOT_FROM,
    NEC_SELECTED_TIME_SLOT_TEXT_ATTR_NAME
} from '../constants';
export const buildAddressFromOrgUnitLocation = (location: OrgUnitLocation): Address => {
    return {
        RecordId: location.PostalAddressId,
        Name: location.OrgUnitName,
        FullAddress: location.Address,
        Street: location.Street,
        StreetNumber: location.StreetNumber,
        City: location.City,
        DistrictName: location.DistrictName,
        BuildingCompliment: location.BuildingCompliment,
        Postbox: location.Postbox,
        ThreeLetterISORegionName: location.Country,
        ZipCode: location.Zip,
        County: location.County,
        CountyName: location.CountyName,
        State: location.State,
        StateName: location.StateName
    };
};

export const doCartOperationWithRetry = async (
    callback: () => Promise<ICartActionResultWithCart>,
    cartState: ICartState
): Promise<ICartActionResult> => {
    let callbackResult = await callback();

    if (callbackResult.status === 'SUCCESS') {
        if (callbackResult.cart) {
            await cartState.updateCart({ newCartObject: callbackResult.cart });
        }
    } else {
        const refreshCartResult = await cartState.refreshCart({});

        if (refreshCartResult.status === 'SUCCESS') {
            callbackResult = await callback();

            if (callbackResult.status === 'SUCCESS') {
                if (callbackResult.cart) {
                    await cartState.updateCart({ newCartObject: callbackResult.cart });
                }
            }
        }
    }

    return {
        status: callbackResult.status,
        substatus: callbackResult.substatus,
        errorDetails: callbackResult.errorDetails,
        validationResults: callbackResult.validationResults
    };
};
export const addProductToCart = async (
    actionContext: IActionContext,
    input: {
        product: SimpleProduct;
        count?: number;
        location?: OrgUnitLocation;
        additionalProperties?: object;
        availableQuantity?: number;
        enableStockCheck?: boolean;
        isAddEmailDeliveryItemToCart?: boolean;
        isPriceKeyedIn?: boolean;
        customPrice?: number;
        deliveryMode?: string;
        shouldSkipSiteSettings?: boolean;
        catalogId?: number;
        retailMultiplePickupModeEnabled?: boolean;
        attributes: AttributeValue[];
    },
    cartState: ICartState
): Promise<ICartActionResult> => {
    const cartLine: CartLine = {
        CatalogId: input.catalogId ?? MsDyn365.getCatalogId(actionContext.requestContext),
        Description: input.product.Description,
        // TODO: Investigate this value and what it represents
        EntryMethodTypeValue: 3,
        ItemId: input.product.ItemId,
        ProductId: input.product.RecordId,
        Quantity: input.count || 1,
        TrackingId: '',
        UnitOfMeasureSymbol: input.product.DefaultUnitOfMeasure,
        IsPriceKeyedIn: input.isPriceKeyedIn,
        IsGiftCardLine: input.product.IsGiftCard,
        Price: input.customPrice ? input.customPrice : input.product.Price,
        AttributeValues: input.attributes
    };

    if (input.location) {
        if (!actionContext.requestContext.channel) {
            return { status: 'FAILED' };
        }

        // If curbside pick is not available use the default one
        if (input.deliveryMode !== undefined) {
            cartLine.DeliveryMode = input.deliveryMode;
        } else {
            cartLine.DeliveryMode = input.retailMultiplePickupModeEnabled
                ? undefined
                : actionContext.requestContext.channel.PickupDeliveryModeCode;
        }

        cartLine.FulfillmentStoreId = input.location.OrgUnitNumber;
        cartLine.WarehouseId = input.location.InventoryLocationId;
        cartLine.ShippingAddress = buildAddressFromOrgUnitLocation(input.location);
    }

    if (input.isAddEmailDeliveryItemToCart) {
        if (!actionContext.requestContext.channel) {
            return { status: 'FAILED' };
        }

        cartLine.DeliveryMode = actionContext.requestContext.channel.EmailDeliveryModeCode;
    }

    // Check if the product is service or not by product type
    const isAddServiceItemToCart = input.product.ItemTypeValue === ReleasedProductType.Service;

    // @ts-expect-error
    const orderQuantityLimitsFeatureIsEnabled: boolean = !!input.additionalProperties?.orderQuantityLimitsFeatureIsEnabled;

    const shouldSkipSiteSettings: boolean = false;
    return doCartOperationWithRetry(
        () =>
            addProductToCartInternal(
                cartState.cart,
                cartLine,
                actionContext,
                orderQuantityLimitsFeatureIsEnabled,
                input.availableQuantity,
                input.enableStockCheck,
                isAddServiceItemToCart,
                shouldSkipSiteSettings
            ),
        cartState
    );
};

export const validateMixedCartErrors = (
    cartLines: CartLine[],
    products: SimpleProduct[],
    catalogs: ProductCatalog[],
    errorMessage: string
): string[] => {
    if (!cartLines?.length) {
        return [];
    }

    const catalogProductMap: Record<number, string[]> = {};

    cartLines.map(cartLine => {
        const product = products?.find(pr => pr.RecordId === cartLine.ProductId);
        if (cartLine.CatalogId !== undefined) {
            catalogProductMap[cartLine.CatalogId] = catalogProductMap[cartLine.CatalogId] || [];
            catalogProductMap[cartLine.CatalogId].push(product?.Name || (cartLine?.ProductId?.toString() ?? ''));
        }
    });

    if (Object.keys(catalogProductMap).length > 1) {
        return [
            errorMessage,
            ...Object.entries(catalogProductMap).map(([catalogId, productNames]) => {
                const catalog = catalogs?.find(ct => ct.RecordId === parseInt(catalogId, 10));
                return `${catalog ? catalog.Name : catalogId}: ${productNames.join(', ')}`;
            })
        ];
    }

    return [];
};

export const validateCartTimeSlotExpiration = (cartLines: CartLine[], products: SimpleProduct[], errorMessage: string): string[] => {
    if (!cartLines?.length) {
        return [];
    }

    const errors: string[] = [];
    const currentTime = new Date(); // Current user computer time

    cartLines.map(cartLine => {
        const timeFromValue = getAttributeTextValueByName(NEC_SELECTED_TIME_SLOT_FROM, cartLine?.AttributeValues!);
        if (timeFromValue) {
            const validationResult = validateTimeSlotValue(timeFromValue, currentTime, 60);
            if (!validationResult.valid) {
                const timeText = getAttributeTextValueByName(NEC_SELECTED_TIME_SLOT_TEXT_ATTR_NAME, cartLine?.AttributeValues!);
                const product = products?.find(pr => pr.RecordId === cartLine.ProductId);
                let productName = product?.Name || (cartLine.ProductId?.toString() ?? '');
                if (timeText) {
                    productName = `${productName} - ${timeText}`;
                }
                errors.push(`${productName}: ${validationResult.message}`);
            }
        }
    });

    if (errors.length) {
        return [errorMessage, ...errors];
    }

    return [];
};

export const validateCartFringeSelections = (cartLines: CartLine[], products: SimpleProduct[], errorMessage: string): string[] => {
    if (!cartLines?.length) {
        return [];
    }

    const errors: string[] = [];

    cartLines.forEach(cartLine => {
        const fringeDescription = getAttributeTextValueByName(NEC_SELECTED_FRINGE_DESCRIPTION_ATTR_NAME, cartLine.AttributeValues ?? []);

        const hasFringeSelection = !!(fringeDescription && fringeDescription.trim());

        if (!hasFringeSelection) {
            const product = products?.find(pr => pr.RecordId === cartLine.ProductId);
            const productName = product?.Name || (cartLine.ProductId?.toString() ?? 'Unknown product');
            errors.push(`${productName}: fringe selection is missing`);
        }
    });

    return errors.length ? [errorMessage, ...errors] : [];
};

export const refreshCartLinesPrices = async (cartState: ICartState | undefined, expiryTimeInMs: number): Promise<void> => {
    const cartLines = cartState?.cart?.CartLines ?? [];
    if (cartLines.length && expiryTimeInMs > 0) {
        try {
            const currentIsoTimestamp = new Date().toISOString();

            const isExiredTimestamp = (timeStamp: string, expiryMs: number): boolean =>
                !timeStamp || Date.now() - Date.parse(timeStamp) > expiryMs;

            const linesWithExpiredPrice = cartLines.filter(line => {
                const updateTime = getAttributeTextValueByName(NEC_LAST_PRICE_UPDATE_TIME, line.AttributeValues ?? []);
                return isExiredTimestamp(updateTime, expiryTimeInMs);
            });

            if (linesWithExpiredPrice?.length) {
                const linesToUpdate = linesWithExpiredPrice.map(line => {
                    const attributeValues = line.AttributeValues ?? [];
                    addOrUpdateTextAttribute(attributeValues, NEC_LAST_PRICE_UPDATE_TIME, currentIsoTimestamp);
                    return { ...line, AttributeValues: attributeValues };
                });

                await cartState!.updateCartLines({ updatedCartLinesObject: linesToUpdate });
            }
        } catch (error) {
            console.error('Cart price timestamp refresh failed', error);
            cartState?.refreshCart({});
        }
    }
};
